import {
  FC,
  useCallback,
  ChangeEvent,
  useState,
  DragEvent,
  useEffect,
  MouseEvent,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import styled, { css } from "styled-components";
import { isObject } from "lodash";

import { Label } from "@/components/Forms";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { SrOnly } from "@/components/Layout/Parts";
import { ReactComponent as ImgIcon } from "@/assets/icons/img-placeholder.svg";
import { ReactComponent as VideoIcon } from "@/assets/icons/video-placeholder.svg";
import { ReactComponent as UploadIcon } from "@/assets/icons/upload.svg";
import { ReactComponent as FilePlaceholder } from "@/assets/icons/file-placeholder.svg";

import Text from "@/components/Text";
import useAlerts from "@/hooks/useAlerts";
import { MediaResource } from "@/types/resources";
import useDidUpdate from "@/hooks/useDidUpdate";
import ProgressBar from "../ProgressBar";
import { uploadFileToS3 } from "@/utils/s3";

export const Dropzone = styled.div<{ $dragging: boolean }>`
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.gray};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  ${({ theme, $dragging }) =>
    $dragging &&
    css`
      border-color: ${theme.colors.purpleLight};
    `}
`;

export const Title = styled(Text)`
  margin: 10px 0 6px;
`;

export const Notice = styled(Text)`
  opacity: 0.5;
  text-align: center;
`;

const Preview = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  padding: 16px;
  position: relative;
`;

const Img = styled.img`
  max-width: 128px;
  height: auto;
  margin-right: 16px;
  object-fit: cover;
`;

const FileInfo = styled.div`
  flex: 1;
  text-align: left;
  padding-left: 16px;
`;

const FileName = styled(Text)`
  margin-bottom: 8px;
`;

const ClearButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  transition: 0.2s ease-out opacity;
  &:hover {
    opacity: 0.75;
  }
`;

export const InnerLabel = styled(Text)`
  cursor: pointer;
`;

const StyledProgressBar = styled(ProgressBar)`
  height: 8px;
`;

const MultipleProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  name?: string;
  label?: string;
  pathName?: string;
  existingMedia?: MediaResource;
  accept?: string;
  fileType: "image" | "other" | "video";
  notes?: string;
  className?: string;
  multiple?: boolean;
  onFilesAdded?: (files?: FileList | null, fieldsIndex?: number) => void;
  fieldsIndex?: number;
  subLabel?: string;
}

const FileField: FC<Props> = ({
  name = "image",
  pathName,
  label,
  existingMedia,
  accept = "image/*",
  fileType = "image",
  notes = "JPG, PNG, GIF up to 10MB",
  fieldsIndex,
  className,
  multiple = false,
  subLabel,
  onFilesAdded,
}) => {
  const [dragging, setDragging] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!!!existingMedia);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProggress, setUploadProgress] = useState<
    | number
    | {
        [key: number]: number;
      }
  >(0);

  const alerts = useAlerts();

  const fileIcon = () => {
    switch (fileType) {
      case "image":
        return <ImgIcon />;
      case "video":
        return <VideoIcon />;
      default:
        return <UploadIcon />;
    }
  };

  const fileTypeName = () => {
    switch (fileType) {
      case "image":
        return "image";
      case "video":
        return "video";
      default:
        return "file";
    }
  };

  const {
    control,
    resetField,
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    register(name);
  }, [register, name]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue(name, e.target.files?.[0] || null);
      onFilesAdded?.(e.target.files, fieldsIndex);
    },
    [setValue, name, onFilesAdded, fieldsIndex]
  );

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.items?.length) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (e.dataTransfer?.files?.length) {
      const droppedFile = e.dataTransfer.files[0];
      if (
        fileType === "image" &&
        ![
          "image/png",
          "image/gif",
          "image/jpg",
          "image/jpeg",
          "image/svg+xml",
        ].includes(droppedFile.type)
      ) {
        return alerts.error(
          "Invalid file type!",
          "Please use image of type jpg/gif/png/svg."
        );
      } else if (
        fileType === "video" &&
        !["video/mp4"].includes(droppedFile.type)
      ) {
        return alerts.error(
          "Invalid file type!",
          "Please use video of type mp4"
        );
      }
      setValue(name, droppedFile);
      onFilesAdded?.(e.dataTransfer?.files, fieldsIndex);
      e.dataTransfer?.clearData();
    }
  };

  const onClearImage = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isEditMode) {
      setIsEditMode(true);
    } else {
      resetField(name);
    }
  };

  const file = useWatch({ control, name }) as File | null;

  const previewable = file ? file : !isEditMode ? existingMedia : null;

  const src = file
    ? URL.createObjectURL(file)
    : existingMedia?.versions?.original || "";

  const uploadToS3 = async () => {
    try {
      setUploadProgress(0);
      setIsUploading(true);

      const path = await uploadFileToS3(
        file!,
        (progressEvent: ProgressEvent) => {
          setUploadProgress(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }
      );

      if (!path) {
        alerts.error("Failed to upload file", "Please try again");
        setValue(name, null);
        setValue(pathName!, "");
        return setIsUploading(false);
      }

      setValue(pathName!, path);
      setIsUploading(false);
    } catch (err) {
      alerts.error("Failed to upload file", "Please try again");
      setValue(name, null);
      setValue(pathName!, "");
      setIsUploading(false);
    }
  };

  useDidUpdate(() => {
    if (!pathName) return;

    if (!file) {
      return setValue(pathName, "");
    }

    uploadToS3();
  }, [file, pathName]);

  const renderUploadProgress = () => {
    if (!isObject(uploadProggress)) {
      return (
        <StyledProgressBar
          progress={uploadProggress as number}
          color="primary"
          showPercentage
        />
      );
    }
    return (
      <MultipleProgressWrap>
        {Object.keys(uploadProggress).map((key) => (
          <div key={key}>
            <StyledProgressBar
              progress={
                uploadProggress[key as unknown as keyof typeof uploadProggress]
              }
              color="primary"
              showPercentage
            />
          </div>
        ))}
      </MultipleProgressWrap>
    );
  };

  return (
    <div className={className}>
      {!!label && (
        <Label>
          {label}
          {!!subLabel && (
            <Text $color="gray" $type="smallBold" as="span">
              {" "}
              {subLabel}
            </Text>
          )}
        </Label>
      )}
      {isUploading ? (
        renderUploadProgress()
      ) : (
        <>
          {previewable ? (
            <Preview>
              <ClearButton onClick={onClearImage}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12"
                    stroke="#222222"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 12L4 4"
                    stroke="#222222"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ClearButton>
              {fileType === "image" ? (
                <Img src={src} alt="" />
              ) : (
                <FilePlaceholder />
              )}
              <FileInfo>
                <FileName as="div" $type="smallMedium">
                  {previewable.name}
                </FileName>
                <Text as="div" $type="smallMedium" $color="gray">
                  {Math.floor(previewable.size / 1024)}Kb
                </Text>
              </FileInfo>
            </Preview>
          ) : (
            <Dropzone
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              $dragging={dragging}
            >
              {fileIcon()}

              <Title $type="smallBold" $color="gray" as="div">
                Drop {fileTypeName()} here or{" "}
                <InnerLabel
                  as="label"
                  $type="smallBold"
                  $color="purple"
                  htmlFor={`${name}-input`}
                >
                  browse
                  <SrOnly
                    as="input"
                    type="file"
                    accept={accept}
                    id={`${name}-input`}
                    multiple={multiple}
                    onChange={onChange}
                  />
                </InnerLabel>
              </Title>
              <Notice $type="extraSmall" $color="gray" as="div">
                Supports: <span dangerouslySetInnerHTML={{ __html: notes }} />
              </Notice>
            </Dropzone>
          )}
        </>
      )}
      {!!errors[name] && (
        <ValidationMessage>{errors[name]?.message as string}</ValidationMessage>
      )}
    </div>
  );
};

export default FileField;
